/* eslint-disable consistent-return */
/* eslint-disable no-unused-expressions */
import React, { useState, forwardRef, useRef, useMemo } from 'react';
import {
  Box,
  Chip,
  Grid,
  Icon,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Typography,
  makeStyles,
} from '@material-ui/core';
import { GroupedVirtuoso } from 'react-virtuoso';
import { useTranslation } from 'react-i18next';
import clsx from 'clsx';
import { useDialogMessage } from 'app/hooks';
import { groupBy } from 'lodash';
import { format, startOfWeek, endOfWeek, isSameWeek, isSameDay, isValid } from 'date-fns';
import MyIconButton from 'components/IconButton';
import MyIcon from 'components/Icon';
import { useDeepCompareEffect } from 'react-use';
import { SCHEDULE_TAB, SCHEDULE_TAB_TYPE, getScheduleColour } from '../../constants';
import EditScheduleDialog from './EditScheduleDialog';
import ExtendDayDialog from './extendDayDialog';
import MonthView from './MonthView/MonthView';
import WeekView from './WeekView/WeekView';
import YearView from './YearView/YearView';

const WEEK_STARTS_ON = 1;

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    height: '100%',
    minHeight: 0,
    overflow: 'hidden',
    display: 'flex',
    flexDirection: 'column',
  },
  scrollableList: {
    flex: 1,
    minHeight: 0,
    height: '100%',
    overflowY: 'auto',
    scrollbarWidth: 'thin',
    scrollbarColor: '#BFE3F3 transparent',
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: 'transparent',
    },
    '&::-webkit-scrollbar-thumb': {
      background: '#BFE3F3',
      borderRadius: 4,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: '#0B9FD9',
    },
  },
  sectionWrapper: {
    marginBottom: 14,
  },
  weekLabel: {
    fontSize: '1.2rem',
    fontWeight: 600,
    color: '#117CA8',
    marginBottom: 8,
  },
  agendaSectionHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    marginBottom: 8,
  },
  dateCircle: {
    width: '34px',
    height: '34px',
    borderRadius: '50%',
    backgroundColor: '#0B9FD9',
    color: '#FFFFFF',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    fontWeight: 600,
  },
  agendaCard: {
    border: '1px solid #DDE9EF',
    borderRadius: 12,
    marginBottom: 4,
    backgroundColor: '#FFFFFF',
    paddingTop: `${theme.spacing(0.25)}px`,
    paddingBottom: `${theme.spacing(0.25)}px`,
    overflow: 'hidden',
    minWidth: 0,
    transition: 'box-shadow 0.2s ease',
    '& .hiddenAction': {
      opacity: 0,
    },
    '&:hover .hiddenAction': {
      opacity: 1,
    },
    '&:hover': {
      boxShadow: '0px 3px 8px rgba(11, 159, 217, 0.14)',
    },
  },
  agendaListItemIcon: {
    flexShrink: 0,
    minWidth: 40,
  },
  agendaActions: {
    flexShrink: 0,
  },
  agendaListItemText: {
    minWidth: 0,
    overflow: 'hidden',
    flex: '1 1 auto',
  },
  titleRow: {
    minWidth: 0,
    width: '100%',
    flexWrap: 'nowrap',
  },
  titleCell: {
    minWidth: 0,
    overflow: 'hidden',
    flex: '1 1 auto',
  },
  typeChip: {
    height: 22,
    fontSize: '1.1rem',
    fontWeight: 600,
    borderRadius: 6,
    marginLeft: 12,
    color: '#FFFFFF',
    flexShrink: 0,
  },
  doneIcon: {
    marginLeft: 8,
    marginRight: 4,
    fontSize: '2rem',
    color: '#8CA4B3',
    cursor: 'pointer',
    flexShrink: 0,
  },
  doneIconActive: {
    color: '#ACDA0E',
  },
  compactText: {
    display: 'block',
    width: '100%',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
}));

const ListItems = (props) => {
  const { data, onDeleteSchedule, handleEditSchedule, tab, focusMonth, onMonthClick } = props;
  const classes = useStyles(props);
  const { t } = useTranslation();
  const dialog = useDialogMessage();
  const [openDialog, setOpenDialog] = useState(false);
  const [editData, setEditData] = useState();
  const [dayDialogOpen, setDayDialogOpen] = useState(false);
  const [dayDialogDate, setDayDialogDate] = useState(null);
  const virtuoso = useRef(null);

  const transferData = useMemo(() => {
    const validSchedules = (data || []).filter((item) => {
      if (!item?.scheduleDate) return false;
      return isValid(new Date(item.scheduleDate));
    });
    return Object.entries(groupBy(validSchedules, (item) => item.scheduleDate)).sort(
      (a, b) => new Date(a[0]).getTime() - new Date(b[0]).getTime(),
    );
  }, [data]);

  const isYearTab = tab === SCHEDULE_TAB.YEAR;
  const isWeekTab = tab === SCHEDULE_TAB.WEEK;
  const isMonthTab = tab === SCHEDULE_TAB.MONTH;
  const isAgendaTab = !isWeekTab && !isMonthTab && !isYearTab;

  const dayDialogTasks = useMemo(() => {
    if (!dayDialogOpen || !dayDialogDate || !isValid(new Date(dayDialogDate))) return [];
    const dialogDay = new Date(dayDialogDate);
    return (data || []).filter((item) => {
      if (!item?.scheduleDate) return false;
      const itemDate = new Date(item.scheduleDate);
      return isValid(itemDate) && isSameDay(itemDate, dialogDay);
    });
  }, [dayDialogOpen, dayDialogDate, data]);

  useDeepCompareEffect(() => {
    if (!dayDialogOpen) return;
    if (dayDialogTasks.length === 0) {
      setDayDialogOpen(false);
      setDayDialogDate(null);
    }
  }, [dayDialogOpen, dayDialogTasks]);

  useDeepCompareEffect(() => {
    if (!isAgendaTab || !transferData.length || !virtuoso.current) return;

    virtuoso.current.scrollToIndex({
      index: 0,
      align: 'start',
      behavior: 'auto',
    });
  }, [data, focusMonth, isAgendaTab]);

  const confirmDeleteSchedule = (event, value) => {
    event.stopPropagation();
    const type = SCHEDULE_TAB_TYPE[value.type]?.label;
    dialog
      .confirm(t('dashboard:DELETE_SCHEDULE_MESSAGE', { type }), t('dashboard:DELETE_SCHEDULE_TITLE', { type }), {
        buttons: [
          { label: t('BUTTON_CANCEL') },
          { label: t('BUTTON_CONFIRM'), variant: 'contained', color: 'primary' },
        ],
      })
      .on((label) => {
        if (label === t('BUTTON_CONFIRM')) {
          onDeleteSchedule(value?.id);
        }
        dialog.close();
      });
  };

  const handleOpenDialog = (value) => {
    setEditData(value);
    setOpenDialog(true);
  };

  const onCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleOpenDayDialog = (date) => {
    setDayDialogDate(date);
    setDayDialogOpen(true);
  };

  const handleCloseDayDialog = () => {
    setDayDialogOpen(false);
    setDayDialogDate(null);
  };

  const handleToggleDone = (event, value) => {
    event.stopPropagation();
    if (!value?.id) return;
    handleEditSchedule(
      {
        colour: value.colour,
        scheduleDate: value.scheduleDate,
        title: value.title,
        detail: value.detail,
        type: value.type,
        isEnableRepeat: value.isEnableRepeat,
        repeatInterval: value.repeatInterval,
        repeatType: value.repeatType,
        repeatEndDate: value.repeatEndDate,
        isDone: !value.isDone,
      },
      value.id,
    );
  };

  const listComponent = {
    List: forwardRef(({ style, children }, listRef) => {
      return (
        <List style={{ ...style }} component="div" ref={listRef}>
          {children}
        </List>
      );
    }),
  };

  const renderAgendaItem = (value) => {
    const typeInfo = SCHEDULE_TAB_TYPE[value.type] || {};
    const itemColour = getScheduleColour(value);
    return (
      <ListItem
        key={value?.id}
        button
        className={clsx(classes.agendaCard)}
        style={{ borderLeft: `4px solid ${itemColour}` }}
        onClick={() => handleOpenDialog(value)}
      >
        <ListItemIcon className={clsx(classes.agendaListItemIcon)}>
          <Icon style={{ color: itemColour }}>{typeInfo.icon}</Icon>
        </ListItemIcon>
        <ListItemText
          className={clsx(classes.agendaListItemText)}
          primary={
            <Grid container alignItems="center" wrap="nowrap" className={clsx(classes.titleRow)}>
              <Grid item xs zeroMinWidth className={clsx(classes.titleCell)}>
                <Typography
                  variant="subtitle2"
                  className={clsx(classes.compactText)}
                  style={{ color: itemColour, fontWeight: 650 }}
                  title={value?.title}
                >
                  {value?.title}
                </Typography>
              </Grid>
              <Grid item>
                <Chip
                  label={typeInfo.label}
                  size="small"
                  className={clsx(classes.typeChip)}
                  style={{ backgroundColor: itemColour }}
                />
              </Grid>
            </Grid>
          }
        />
        <Icon
          className={clsx(classes.doneIcon, value?.isDone && classes.doneIconActive)}
          aria-label={value?.isDone ? t('dashboard:SCHEDULE_MARK_NOT_DONE') : t('dashboard:SCHEDULE_MARK_DONE')}
          onClick={(event) => handleToggleDone(event, value)}
        >
          {value?.isDone ? 'check_circle' : 'radio_button_unchecked'}
        </Icon>
        <div className={clsx('hiddenAction', 'ml-12 flex flex-row', classes.agendaActions)}>
          <MyIconButton onClick={(e) => confirmDeleteSchedule(e, value)} color="primary" theme="cinnamon">
            <MyIcon icon="trash-alt" />
          </MyIconButton>
        </div>
      </ListItem>
    );
  };

  const renderWeekLabel = (date) => {
    const start = startOfWeek(new Date(date), { weekStartsOn: WEEK_STARTS_ON });
    const end = endOfWeek(new Date(date), { weekStartsOn: WEEK_STARTS_ON });
    return (
      <Typography variant="caption" className={clsx(classes.weekLabel, 'cursor-default')}>
        {`Week: ${format(start, 'dd MMM')} - ${format(end, 'dd MMM')}`}
      </Typography>
    );
  };

  const renderAgendaList = () => (
    <GroupedVirtuoso
      className={clsx(classes.scrollableList)}
      style={{ height: '100%' }}
      data={transferData}
      components={listComponent}
      ref={virtuoso}
      itemContent={(index) => {
        const key = transferData.length > 0 && transferData[index][0];
        const value = transferData.length > 0 && transferData[index][1];
        const scheduleDate = new Date(key);
        if (!isValid(scheduleDate)) return null;

        return (
          <div key={index} className={clsx(classes.sectionWrapper)}>
            {(index === 0 ||
              !isSameWeek(scheduleDate, new Date(transferData[index - 1][0]), {
                weekStartsOn: WEEK_STARTS_ON,
              })) &&
              renderWeekLabel(key)}
            <Grid
              item
              container
              direction="row"
              justify="space-between"
              alignItems="center"
              className={clsx(classes.agendaSectionHeader)}
            >
              <Grid item container alignItems="center" wrap="nowrap" xs={8}>
                <Grid item>
                  <Box className={clsx(classes.dateCircle)}>
                    <Typography className="cursor-default">{format(scheduleDate, 'd')}</Typography>
                  </Box>
                </Grid>
                <Grid item className="ml-8">
                  <Typography variant="subtitle2" className="cursor-default">
                    {format(scheduleDate, 'EEEE')}
                  </Typography>
                  <Typography variant="caption" color="textSecondary" className="cursor-default">
                    {format(scheduleDate, 'dd MMM yyyy')}
                  </Typography>
                </Grid>
              </Grid>
              <Grid item>
                <Typography variant="caption" color="textSecondary">
                  {`${value.length} item${value.length > 1 ? 's' : ''}`}
                </Typography>
              </Grid>
            </Grid>

            {value.map((v) => renderAgendaItem(v))}
          </div>
        );
      }}
    />
  );

  return (
    <Grid container className={clsx(classes.root)}>
      <Grid xs={12} item className={clsx(classes.root)}>
        {isWeekTab && <WeekView data={data} onItemClick={handleOpenDialog} onOpenDayDialog={handleOpenDayDialog} />}
        {isMonthTab && (
          <MonthView
            data={data}
            focusMonth={focusMonth}
            onItemClick={handleOpenDialog}
            onOpenDayDialog={handleOpenDayDialog}
          />
        )}
        {isYearTab && (
          <YearView
            data={data}
            focusYear={focusMonth}
            onItemClick={handleOpenDialog}
            onOpenDayDialog={handleOpenDayDialog}
            onMonthClick={onMonthClick}
          />
        )}
        {isAgendaTab && renderAgendaList()}
        {editData && (
          <EditScheduleDialog
            open={openDialog}
            onClose={onCloseDialog}
            handleEditSchedule={handleEditSchedule}
            schedule={editData}
          />
        )}
        <ExtendDayDialog
          open={dayDialogOpen}
          onClose={handleCloseDayDialog}
          date={dayDialogDate}
          tasks={dayDialogTasks}
          renderItem={renderAgendaItem}
        />
      </Grid>
    </Grid>
  );
};

export default ListItems;
